import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaBan,
  FaCalendarAlt,
  FaChevronDown,
  FaClock,
  FaFileAlt,
  FaHeadset,
  FaSyncAlt,
  FaWallet,
} from "react-icons/fa";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import RefundHeroVisual from "../../components/refund/RefundHeroVisual";
import { fetchPublicCmsPage } from "../../services/cms.service";
import { buildRefundPageModel } from "../../utils/refundPageCms";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/refund_policy.scss";

const SECTION_ICONS = {
  file: FaFileAlt,
  ban: FaBan,
  wallet: FaWallet,
  sync: FaSyncAlt,
  clock: FaClock,
  headset: FaHeadset,
};

function formatBadgeDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

function fallbackTodayLabel() {
  return new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getStickyHeaderOffset() {
  const header = document.querySelector(".hub-home-header");
  return (header?.getBoundingClientRect().height || 72) + 16;
}

function scrollElementIntoViewWithOffset(el) {
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - getStickyHeaderOffset();
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
}

export default function RefundCancellationPolicy() {
  const [page, setPage] = useState(null);
  const [openIds, setOpenIds] = useState(() => new Set(["overview"]));
  const [activeNavId, setActiveNavId] = useState("overview");
  const pendingScrollIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchPublicCmsPage("REFUND_POLICY");
        if (!cancelled) setPage(data);
      } catch {
        if (!cancelled) setPage(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const model = useMemo(() => buildRefundPageModel(page), [page]);
  const lastUpdated = formatBadgeDate(model.updatedAt) || fallbackTodayLabel();
  const effectiveDate = formatBadgeDate(model.effectiveDate) || lastUpdated;

  useEffect(() => {
    const ids = model.sections.map((s) => s.id);
    if (!ids.length) {
      setOpenIds(new Set());
      setActiveNavId(null);
      return;
    }
    setOpenIds(new Set(ids));
    setActiveNavId((prev) => (ids.includes(prev) ? prev : ids[0]));
  }, [model.sections]);

  useEffect(() => {
    const id = pendingScrollIdRef.current;
    if (!id || !openIds.has(id)) return;
    pendingScrollIdRef.current = null;
    const run = () => {
      scrollElementIntoViewWithOffset(document.getElementById(`refund-section-${id}`));
    };
    requestAnimationFrame(() => requestAnimationFrame(run));
  }, [openIds]);

  const toggleSection = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setActiveNavId(id);
  };

  const scrollToSection = (id) => {
    setActiveNavId(id);
    setOpenIds((prev) => new Set(prev).add(id));
    pendingScrollIdRef.current = id;
    if (openIds.has(id)) {
      pendingScrollIdRef.current = null;
      scrollElementIntoViewWithOffset(document.getElementById(`refund-section-${id}`));
    }
  };

  return (
    <div className="hub-refund-page">
      <B2BHubHomeHeader />

      <main className="hub-refund-page__main">
        <section className="hub-refund-hero" aria-labelledby="hub-refund-title">
          <div className="container-fluid hub-refund-hero__inner">
            <div className="hub-refund-hero__copy">
              <nav className="hub-refund-breadcrumb" aria-label="Breadcrumb">
                <Link to="/home">Home</Link>
                <span aria-hidden>&gt;</span>
                <span>Policies</span>
                <span aria-hidden>&gt;</span>
                <span aria-current="page">Refund &amp; Cancellation Policy</span>
              </nav>

              <h1 id="hub-refund-title" className="hub-refund-hero__title">
                Refund &amp; Cancellation Policy
              </h1>
              <p className="hub-refund-hero__lead">{model.intro}</p>

              <div className="hub-refund-hero__meta">
                <span className="hub-refund-hero__meta-badge">
                  <FaCalendarAlt aria-hidden />
                  <span>
                    <strong>Last Updated:</strong> {lastUpdated}
                  </span>
                </span>
                <span className="hub-refund-hero__meta-badge">
                  <FaCalendarAlt aria-hidden />
                  <span>
                    <strong>Effective Date:</strong> {effectiveDate}
                  </span>
                </span>
              </div>
            </div>

            <RefundHeroVisual imageUrl={model.heroBackgroundUrl} />
          </div>
        </section>

        <section className="hub-refund-body">
          <div className="container-fluid hub-refund-body__inner">
            <aside className="hub-refund-toc" aria-label="On this page">
              <h2 className="hub-refund-toc__heading">On this page</h2>
              <ol className="hub-refund-toc__list">
                {model.sections.map((section, index) => (
                  <li key={section.id}>
                    <button
                      type="button"
                      className={`hub-refund-toc__link${
                        activeNavId === section.id ? " is-active" : ""
                      }`}
                      onClick={() => scrollToSection(section.id)}
                    >
                      <span className="hub-refund-toc__num">{index + 1}.</span>
                      {section.title}
                    </button>
                  </li>
                ))}
              </ol>
            </aside>

            <div className="hub-refund-content">
              <div className="hub-refund-accordion" role="list">
                {model.sections.map((section, index) => {
                  const Icon = SECTION_ICONS[section.icon] || FaFileAlt;
                  const isOpen = openIds.has(section.id);
                  const isHelp = section.id === "need-help";
                  return (
                    <article
                      key={section.id}
                      id={`refund-section-${section.id}`}
                      className={`hub-refund-card${isOpen ? " is-open" : ""}${
                        isHelp ? " hub-refund-card--help" : ""
                      }`}
                      role="listitem"
                    >
                      <button
                        type="button"
                        className="hub-refund-card__trigger"
                        aria-expanded={isOpen}
                        aria-controls={`refund-panel-${section.id}`}
                        onClick={() => toggleSection(section.id)}
                      >
                        <span className="hub-refund-card__num" aria-hidden>
                          {index + 1}
                        </span>
                        <span className="hub-refund-card__icon" aria-hidden>
                          <Icon />
                        </span>
                        <span className="hub-refund-card__title">{section.title}</span>
                        <FaChevronDown className="hub-refund-card__chevron" aria-hidden />
                      </button>
                      <div
                        id={`refund-panel-${section.id}`}
                        className="hub-refund-card__panel"
                        hidden={!isOpen}
                      >
                        <div className="hub-refund-card__panel-inner">
                          <div
                            className="hub-refund-card__body cms-rendered-html"
                            dangerouslySetInnerHTML={{ __html: section.body }}
                          />
                          {isHelp ? (
                            <Link to="/contact" className="hub-refund-card__help-btn">
                              <FaHeadset aria-hidden />
                              Contact Support
                            </Link>
                          ) : null}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      </main>

      <HubHomeFooter />
    </div>
  );
}
